<template>
  <div class="map-view">
    <!-- 返回按钮 -->
    <div class="back-button-container">
      <button class="back-button" @click="goBack">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
    
    <!-- 城市信息显示 -->
    <div class="city-info-container" v-if="currentCity">
      <div class="city-info">
        <span class="city-name">{{ currentCity.englishName }}</span>
        <span class="city-scenario">({{ displaySSP }}-{{ displayYear }})</span>
      </div>
    </div>
    
    <div class="map-container" ref="mapContainer">
      <!-- 地图将在这里渲染 -->
    </div>
    
    <div class="legend" v-if="isLayerVisible('accessibility')">
      <h3 class="legend-title">
        Urban blue-green space<br>
        accessibility
      </h3>
      <div class="legend-items">
        <div class="legend-item" v-for="(item, index) in legendItems" :key="index">
          <div class="legend-color" :style="{ backgroundColor: item.color }"></div>
          <div>{{ item.displayText }}</div>
        </div>
      </div>
    </div>
    
    <!-- 悬停弹出框 -->
    <div id="popup" ref="popup" class="ol-popup">
      <div class="popup-content" v-if="popupInfo">
        <div class="hover-info-item">
          <span class="hover-label">Accessibility:</span>
          <span class="hover-value">{{ popupInfo.accessibilityValue }}</span>
          </div>
        <div class="hover-info-item">
          <span class="hover-label">Longitude:</span>
          <span class="hover-value">{{ popupInfo.longitude }}</span>
          </div>
        <div class="hover-info-item">
          <span class="hover-label">Latitude:</span>
          <span class="hover-value">{{ popupInfo.latitude }}</span>
        </div>
      </div>
    </div>
    
    <div class="loading-overlay" v-if="isLoading">
      <div class="loading-content">
        <div class="spinner"></div>
        <p>Loading data...</p>
      </div>
    </div>
    
    <div v-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="retryLoad">Retry</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import { useMapStore } from '../store/mapStore'
import { useRouter, useRoute } from 'vue-router'
import * as shapefile from 'shapefile'

// 导入OpenLayers必要的组件
import 'ol/ol.css'
import Map from 'ol/Map'
import View from 'ol/View'
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import { XYZ } from 'ol/source'
import { fromLonLat, transform } from 'ol/proj'
import VectorSource from 'ol/source/Vector'
import { GeoJSON } from 'ol/format'
import { Style, Fill, Stroke } from 'ol/style'
import { defaults as defaultControls } from 'ol/control'
import Overlay from 'ol/Overlay'
import { defaults as defaultInteractions } from 'ol/interaction'

// 是否启用详细日志（开发时设为true，生产环境设为false）
const ENABLE_VERBOSE_LOGGING = process.env.NODE_ENV !== 'production';

// 日志辅助函数，根据控制变量输出日志
const logInfo = (message, ...args) => {
  if (ENABLE_VERBOSE_LOGGING) {
    console.log(message, ...args);
  }
};

// 日志辅助函数，始终输出错误信息
const logError = (message, ...args) => {
  console.error(message, ...args);
};

// 日志辅助函数，始终输出警告信息
const logWarn = (message, ...args) => {
  console.warn(message, ...args);
};

export default {
  name: 'MapView',
  setup() {
    const mapStore = useMapStore()
    const mapContainer = ref(null)
    const map = ref(null)
    const vectorLayer = ref(null)
    const error = ref(null)
    const popup = ref(null)
    const popupOverlay = ref(null)
    const popupInfo = ref(null)
    const router = useRouter()
    const route = useRoute()
    
    // 城市映射数据
    const cityMapping = ref({})
    const currentCity = ref(null)
    
    // 从store获取状态
    const isLoading = computed(() => mapStore.isLoading)
    
    // 固定的可达性等级 - 使用英文标签和统一区间
    const populationLevels = ref([
      { min: 0, max: 20.89, label: "Very Low", color: "#1E90FF" },      // 蓝色
      { min: 20.89, max: 424.64, label: "Low", color: "#90EE90" },      // 绿色
      { min: 424.64, max: 828.39, label: "Medium", color: "#FFFF00" },  // 黄色
      { min: 828.39, max: 1636.64, label: "High", color: "#FFA500" },   // 橙色
      { min: 1636.64, max: Infinity, label: "Very High", color: "#FF0000" } // 红色
    ])
    
    // 计算图例显示文本
    const legendItems = computed(() => {
      return populationLevels.value.map((level, index) => ({
        ...level,
        displayText: level.max === Infinity 
          ? `${level.label} (> ${level.min})` 
          : `${level.label} (${level.min} - ${level.max})`
      }))
    })
    
    // 显示的SSP信息
    const displaySSP = computed(() => {
      const ssp = route.query.ssp || 'SSP1'
      return ssp === 'base' ? 'Base Period' : ssp
    })
    
    // 显示的年份信息
    const displayYear = computed(() => {
      return route.query.year || '2030'
    })
    
    // 初始化地图
    const initMap = () => {
      try {
        logInfo('使用Web Mercator (EPSG:3857)投影系统');
        
        // 创建OSM底图 - 使用自定义样式URL，白色道路
        const osmLayer = new TileLayer({
          source: new XYZ({
            url: 'https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
            maxZoom: 19
          }),
          visible: true
        })
        
        // 备用底图 - 另一个白色道路样式源
        const backupLayer = new TileLayer({
          source: new XYZ({
            url: 'https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png',
            maxZoom: 19,
            attributions: 'Map tiles by CartoDB, under CC BY 3.0. Data by OpenStreetMap, under ODbL.'
          }),
          visible: false
        })
        
        // 创建矢量图层用于显示可达性数据
        const vectorSource = new VectorSource()
        vectorLayer.value = new VectorLayer({
          source: vectorSource,
          style: styleFunction,
          visible: true
        })
        
        // 默认中心点坐标 (北京)
        const defaultCenter = [116.4074, 39.9042]
        const defaultCenterMercator = fromLonLat(defaultCenter)
        
        // 创建地图，使用Web Mercator投影
        map.value = new Map({
          target: mapContainer.value,
          layers: [osmLayer, backupLayer, vectorLayer.value],
          view: new View({
            projection: 'EPSG:3857',  // 使用Web Mercator投影
            center: defaultCenterMercator,  // 默认中心点，后续会根据城市数据调整
            zoom: 6,  // 降低初始缩放级别
            minZoom: 4
          }),
          controls: defaultControls({
            attributionOptions: {
              collapsible: false
            }
          }),
          interactions: defaultInteractions({
            mouseWheelZoom: true,
            doubleClickZoom: true,
            dragPan: true
          })
        })
        
        // 监听主图层加载错误，自动切换到备用图层
        osmLayer.getSource().on('tileloaderror', () => {
          logWarn('主要底图瓦片加载失败，切换到备用图层');
          
          // 检查是否已经显示了备用图层
          const layers = map.value.getLayers().getArray();
          const backupLayerInstance = layers.find(l => l.get('name') === 'backup');
          const osmLayerInstance = layers.find(l => l.get('name') === 'osm');
          
          if (backupLayerInstance && osmLayerInstance && osmLayerInstance.getVisible()) {
            // 隐藏主图层
            osmLayerInstance.setVisible(false);
            // 显示备用图层
            backupLayerInstance.setVisible(true);
          }
        });
        
        // 初始化弹出框
        popupOverlay.value = new Overlay({
          element: popup.value,
          autoPan: false,
          positioning: 'bottom-center',
          offset: [0, -10]
        })
        map.value.addOverlay(popupOverlay.value)
        
        // 添加鼠标移动事件
        map.value.on('pointermove', handlePointerMove)
        
        // 添加点击事件 - 点击时显示弹出框
        map.value.on('click', handleMapClick)
        
        // 给图层添加标识方便后续操作
        osmLayer.set('name', 'osm')
        backupLayer.set('name', 'backup')
        vectorLayer.value.set('name', 'accessibility')
        
        // 加载数据 - 捕获异步错误
        loadCityData().catch(err => {
          logError('加载城市数据失败:', err)
          error.value = 'Failed to load city data, please refresh the page and try again'
          mapStore.setLoading(false)
        })
      } catch (err) {
        logError('初始化地图失败:', err)
        error.value = 'Map initialization failed, please refresh the page and try again'
        mapStore.setLoading(false)
      }
    }
    
    // 根据可达性值生成样式
    const styleFunction = (feature) => {
      
      // 使用A字段数据来确定颜色（蓝绿空间可达性指数）
      const accessibility = feature.get('A') || 0
      
      // 根据可达性值设置颜色
      let color = '#ccc' // 默认灰色
      
      // 使用populationLevels查找对应的颜色
      for (const level of populationLevels.value) {
        if (accessibility >= level.min && accessibility <= level.max) {
          color = level.color
          break
        }
      }
      
      // 设置透明度和边框
      const opacity = '70'; // 降低透明度，使色块更透明
      const strokeColor = '#333333'; // 更深的灰色/黑色边框
      
      // 注意：Y轴方向看起来被拉伸是因为在北京纬度(约40°N)，经度1度实际物理距离约为纬度1度的0.77倍
      // 这是由于地球是球形的，经线在高纬度地区会聚拢。
      // Web Mercator投影(EPSG:3857)会保留这种变形，因为它主要保持角度而非面积。
      return new Style({
        fill: new Fill({
          color: color + opacity // 添加透明度
        }),
        stroke: new Stroke({
          color: strokeColor, // 更深的灰色网格线
          width: 0.2 // 稍微增加线条宽度，使其更明显
        })
      })
    }
    
    // 处理鼠标悬停事件 - 优化版本，使用防抖技术
    const handlePointerMove = (() => {
      let timer = null;
      const delay = 100; // 100毫秒的防抖延迟
      
      return (evt) => {
        try {
          // 阻止默认行为，防止页面滚动
          evt.preventDefault && evt.preventDefault();
          
          // 如果正在拖动地图，不处理悬停
          if (evt.dragging) {
            return;
          }
          
          // 清除已有的定时器
          if (timer) {
            clearTimeout(timer);
          }
          
          // 设置新的定时器
          timer = setTimeout(() => {
            try {
              // 获取鼠标位置对应的像素
              const pixel = map.value.getEventPixel(evt.originalEvent)
              
              // 检查该位置是否有要素
              const hit = map.value.hasFeatureAtPixel(pixel)
              
              // 更新鼠标样式
              map.value.getTargetElement().style.cursor = hit ? 'pointer' : ''
              
              // 如果没有要素，关闭弹窗
              if (!hit) {
                popupOverlay.value.setPosition(undefined)
                popupInfo.value = null
                return
              }
              
              // 获取悬停处的要素
              const feature = map.value.forEachFeatureAtPixel(pixel, feature => feature)
              if (feature) {
                updatePopupInfo(feature, evt.coordinate);
              }
            } catch (error) {
              console.error('指针移动事件处理错误:', error);
            }
          }, delay);
        } catch (error) {
          console.error('指针移动事件错误:', error);
        }
      };
    })();
    
    // 更新弹出框信息 - 提取为单独函数，避免代码重复
    const updatePopupInfo = (feature, coordinate) => {
      const properties = feature.getProperties();
      
      // 使用传入的精确坐标（鼠标位置）
      let coordinates = coordinate;
      let wgs84Coordinates = [0, 0];
      
      if (coordinates) {
        try {
          // 从Web Mercator转换到WGS84 (EPSG:4326)用于显示
          wgs84Coordinates = transform(coordinates, 'EPSG:3857', 'EPSG:4326');
        } catch (e) {
          logError('坐标转换失败', e);
          // 如果坐标转换失败，尝试使用几何中心点
          if (feature.getGeometry()) {
          try {
              coordinates = feature.getGeometry().getInteriorPoint().getCoordinates();
            wgs84Coordinates = transform(coordinates, 'EPSG:3857', 'EPSG:4326');
          } catch (e2) {
            logError('无法获取几何坐标', e2);
            }
          }
        }
      }
      
      // 提取需要的属性数据
      let { pop, A, Id, name, geometry, Longitude, Latitude, ...others } = properties;
      
      // 更新弹出框信息
      popupInfo.value = {
        accessibilityValue: A !== undefined ? Number(A).toFixed(2) : 'N/A',
        longitude: wgs84Coordinates[0].toFixed(6),
        latitude: wgs84Coordinates[1].toFixed(6)
      };
      
      // 设置弹出框位置 - 使用精确的鼠标坐标
      popupOverlay.value.setPosition(coordinates);
    };
    
    // 处理地图点击事件
    const handleMapClick = (evt) => {
      try {
        // 获取点击位置的像素
        const pixel = map.value.getEventPixel(evt.originalEvent);
        
        // 获取点击处的要素
        const feature = map.value.forEachFeatureAtPixel(pixel, feature => feature);
        if (feature) {
          updatePopupInfo(feature, evt.coordinate);
        } else {
          // 如果没有要素，关闭弹窗
          popupOverlay.value.setPosition(undefined);
          popupInfo.value = null;
        }
      } catch (error) {
        console.error('地图点击事件错误:', error);
      }
    };
    
    // 清除悬停处理的定时器
    const clearHoverTimers = () => {
      if (handlePointerMove.timer) {
        clearTimeout(handlePointerMove.timer)
        handlePointerMove.timer = null
      }
    }
    
    // 当组件卸载时清除定时器和事件监听器
    onUnmounted(() => {
      clearHoverTimers()
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    })
    
    // 加载城市映射数据
    const loadCityMapping = async () => {
      try {
        const response = await fetch('/data/city-centers.json')
        if (!response.ok) throw new Error('城市映射文件加载失败')
        const data = await response.json()
        
        // 转换格式，从城市名映射到城市代码的格式
        const mapping = {}
        for (const [cityName, cityData] of Object.entries(data.cities)) {
          mapping[cityData.cityCode] = {
            cityCode: cityData.cityCode,
            chineseName: cityName,
            englishName: cityData.englishName,
            centerCoords: cityData.center
          }
        }
        cityMapping.value = mapping
        logInfo('城市映射数据加载成功，城市总数:', Object.keys(mapping).length)
      } catch (err) {
        logError('加载城市映射失败:', err)
        throw err
      }
    }

    // 根据cityCode获取城市信息
    const getCityInfo = (cityCode) => {
      return cityMapping.value[cityCode] || null
    }

    // 动态加载城市数据
    const loadCityData = async () => {
      try {
        error.value = null
        mapStore.setLoading(true)
        
        // 获取URL参数
        const cityCode = route.params.cityCode
        let ssp = route.query.ssp || 'SSP1'
        const year = route.query.year || '2030'
        
        // 如果是基期，使用"基期"文件夹
        if (ssp === 'base') {
          ssp = '基期'
        }
        
        if (!cityCode) {
          throw new Error('Missing city code parameter')
        }

        // 加载城市映射
        await loadCityMapping()
        
        // 获取城市信息
        currentCity.value = getCityInfo(cityCode)
        if (!currentCity.value) {
          throw new Error(`City information not found for city code ${cityCode}`)
        }

        logInfo(`开始加载城市数据: ${currentCity.value.chineseName}, SSP: ${ssp}, 年份: ${year}`)

        // 构建文件路径
        const cityChineseName = currentCity.value.chineseName
        const shpPath = `/api/detail/${ssp}/${year}/${encodeURIComponent(cityChineseName)}?fileType=shp`
        const dbfPath = `/api/detail/${ssp}/${year}/${encodeURIComponent(cityChineseName)}?fileType=dbf`
        
        let shpArrayBuffer, dbfArrayBuffer, geojson
        
        try {
          // 加载SHP文件
          const shpResponse = await fetch(shpPath)
          if (!shpResponse.ok) {
            throw new Error(`SHP文件加载失败: ${shpResponse.status} ${shpResponse.statusText}`)
          }
          shpArrayBuffer = await shpResponse.arrayBuffer()
          
          // 加载DBF文件
          const dbfResponse = await fetch(dbfPath)
          if (!dbfResponse.ok) {
            throw new Error(`DBF文件加载失败: ${dbfResponse.status} ${dbfResponse.statusText}`)
          }
          dbfArrayBuffer = await dbfResponse.arrayBuffer()
          
          // 使用shapefile读取GeoJSON
          geojson = await shapefile.read(shpArrayBuffer, dbfArrayBuffer)
            
            if (geojson && geojson.features) {
              geojson.features = geojson.features.map((feature, index) => {
                if (feature.id === undefined) {
                feature.id = index + 1
                }
                if (feature.properties && !feature.properties.Id) {
                feature.properties.Id = feature.id
                }
              return feature
            })
          }
        } catch (fetchError) {
          logError('数据文件加载失败:', fetchError)
          throw new Error(`请确保数据文件存在：${shpPath} 和 ${dbfPath}`)
        }
        
        // 存储数据
        mapStore.setMapData(geojson)
        
        // 清除现有图层数据
        vectorLayer.value.getSource().clear()
        
        // 处理GeoJSON数据
        parseAndAddFeatures(geojson)
          
        // 检查特征并调整视图
        const featuresInMap = vectorLayer.value.getSource().getFeatures()
          if (featuresInMap.length > 0) {
          const extent = vectorLayer.value.getSource().getExtent()
          
          if (map.value && extent.some(val => !!val && val !== Infinity && val !== -Infinity)) {
            map.value.getView().fit(extent, {
              padding: [100, 100, 100, 100],
              duration: 1000,
              maxZoom: 12
            })
          } else if (map.value) {
            // 使用城市中心点
            const cityCenter = fromLonLat(currentCity.value.centerCoords)
            map.value.getView().setCenter(cityCenter)
            map.value.getView().setZoom(10)
          }
          } else {
          logError('向量图层中没有特征')
        }
        
        mapStore.setLoading(false)
        logInfo(`${currentCity.value.chineseName}数据加载完成`)
        
      } catch (err) {
        logError('城市数据加载失败:', err)
        error.value = 'Data loading failed: ' + err.message
        mapStore.setError('Data loading failed: ' + err.message)
        mapStore.setLoading(false)
      }
    }
    
    // 共用的GeoJSON解析和特征添加函数
    const parseAndAddFeatures = (geojson) => {
      // 创建GeoJSON解析器 - 源数据使用WGS84 (EPSG:4326)投影，转换到Web Mercator (EPSG:3857)
      const geoJSONParser = new GeoJSON({
        dataProjection: 'EPSG:4326',  // 源数据是WGS84地理坐标系 (经纬度)
        featureProjection: 'EPSG:3857'  // 目标是Web Mercator投影
      });
      
      try {
        // 验证并修复GeoJSON中的坐标问题
        if (geojson && geojson.features) {
          geojson.features = geojson.features.filter(feature => {
            // 过滤掉没有几何信息的特征
            if (!feature || !feature.geometry || !feature.geometry.coordinates) {
              logWarn('过滤掉无效特征 (无几何信息)');
              return false;
            }
            
            // 验证坐标 - 检查投影坐标是否有效
            if (feature.geometry.type === 'Point') {
              // 点几何类型的坐标验证
              _validateProjectedCoordinates(feature.geometry.coordinates);
            } else if (feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiPoint') {
              // 线几何类型的坐标验证
              feature.geometry.coordinates.forEach(coord => _validateProjectedCoordinates(coord));
            } else if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiLineString') {
              // 面或多线的坐标验证
              feature.geometry.coordinates.forEach(ring => {
                ring.forEach(coord => _validateProjectedCoordinates(coord));
              });
            } else if (feature.geometry.type === 'MultiPolygon') {
              // 多面的坐标验证
              feature.geometry.coordinates.forEach(polygon => {
                polygon.forEach(ring => {
                  ring.forEach(coord => _validateProjectedCoordinates(coord));
                });
              });
            }
            
            return true;
          });
        }
        
        // 解析GeoJSON，自动将坐标从WGS84转换为Web Mercator
        const features = geoJSONParser.readFeatures(geojson);
        logInfo('GeoJSON解析成功，特征数量:', features.length);
        
        // 添加特征到图层前检查坐标有效性
        const validFeatures = features.filter(feature => {
          const geometry = feature.getGeometry();
          if (!geometry) return false;
          
          // 获取第一个坐标做验证
          let firstCoord;
          try {
            firstCoord = geometry.getFirstCoordinate();
          } catch (e) {
            logError('无法获取几何坐标', e);
            return false;
          }
          
          // 验证坐标是否有效（不是NaN且在合理范围内）
          if (!firstCoord || firstCoord.some(v => isNaN(v) || !isFinite(v))) {
            logWarn('过滤掉特征 (坐标包含NaN值):', firstCoord);
            return false;
          }
          
          return true;
        });
        
        logInfo(`过滤后的有效特征数量: ${validFeatures.length} / ${features.length}`);
        
        // 添加有效特征到图层
        vectorLayer.value.getSource().addFeatures(validFeatures);
        
        // 分析数据并更新颜色区间
        if (validFeatures.length > 0) {
          analyzeDataAndUpdateColors(validFeatures);
          return true;
        } else {
          logError('没有有效特征可添加到图层');
          return false;
        }
      } catch (error) {
        logError('GeoJSON解析失败:', error);
        return false;
      }
    }
    
    // 辅助函数：验证投影坐标
    const _validateProjectedCoordinates = (coord) => {
      if (!coord || coord.length < 2) return false;
      
      // 检查坐标是否为NaN或无穷大
      if (isNaN(coord[0]) || !isFinite(coord[0])) {
        logWarn('发现无效X坐标，修正为0');
        coord[0] = 0;
      }
      
      if (isNaN(coord[1]) || !isFinite(coord[1])) {
        logWarn('发现无效Y坐标，修正为0');
        coord[1] = 0;
      }
      
      return true;
    }
    
    // 分析数据并更新颜色区间
    const analyzeDataAndUpdateColors = (features) => {
      // 提取所有可达性值（使用A字段）
      const accessibilityValues = features
        .map(feature => parseFloat(feature.get('A') || 0))
        .filter(value => !isNaN(value)); // 过滤掉NaN值
      
      if (accessibilityValues.length === 0) {
        logInfo('没有找到有效的可达性数据');
        return;
      }
      
      // 计算数据统计值
      const min = Math.min(...accessibilityValues);
      const max = Math.max(...accessibilityValues);
      
      logInfo(`可达性数据范围: 最小值=${min}, 最大值=${max}`);
      logInfo('使用固定的统一区间进行颜色分级');
      
      // 更新向量图层样式以应用新的颜色区间
      if (vectorLayer.value) {
        vectorLayer.value.changed();
      }
    };
    
    // 检查图层是否可见
    const isLayerVisible = (layerName) => {
      return mapStore.isLayerVisible(layerName)
    }
    
    // 重试加载
    const retryLoad = () => {
      loadCityData()
    }
    
    // 返回主页面
    const goBack = () => {
      // 构建返回参数，包含城市信息和状态
      const queryParams = {
        mode: 'category'
      };
      
      // 如果有城市信息，传递回去
      if (route.params.cityCode) {
        queryParams.cityCode = route.params.cityCode;
      }
      if (route.query.cityName) {
        queryParams.cityName = route.query.cityName;
      }
      if (route.query.ssp) {
        queryParams.ssp = route.query.ssp;
      }
      if (route.query.year) {
        queryParams.year = route.query.year;
      }
      
      router.push({ path: '/', query: queryParams }).catch(err => {
        console.warn('返回路由导航错误:', err);
      });
    }
    
    // 添加全局未处理Promise拒绝的错误处理
    const handleUnhandledRejection = (event) => {
      console.error('MapView未处理的Promise拒绝:', event.reason);
      event.preventDefault(); // 阻止默认的错误处理
    };
    
    onMounted(() => {
      // 监听未处理的Promise拒绝
      window.addEventListener('unhandledrejection', handleUnhandledRejection);
      
      initMap()
      
      // 阻止地图容器上的滚轮事件导致页面滚动
      if (mapContainer.value) {
        mapContainer.value.addEventListener('wheel', (e) => {
          e.preventDefault();
        }, { passive: false });
        
        // 阻止弹出框上的滚动事件冒泡到页面
        if (popup.value) {
          popup.value.addEventListener('wheel', (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, { passive: false });
          
          popup.value.addEventListener('touchmove', (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, { passive: false });
        }
      }
    })
    
    // 监听图层可见性变化
    watch(() => mapStore.visibleLayers, (newLayers) => {
      if (map.value) {
        const layers = map.value.getLayers().getArray()
        layers.forEach(layer => {
          const name = layer.get('name')
          if (name && name !== 'osm') {
            layer.setVisible(newLayers.includes(name))
          }
        })
      }
    }, { deep: true })
    
    return {
      mapContainer,
      isLoading,
      populationLevels,
      error,
      retryLoad,
      popup,
      popupInfo,
      isLayerVisible,
      legendItems,
      goBack,
      currentCity,
      displaySSP,
      displayYear
    }
  }
}
</script>

<style scoped>
.map-view {
  width: 100%;
  height: 100vh;
  position: relative;
  /* 禁止滚动 */
  overflow: hidden;
  touch-action: none;
  /* 防止拖动和选择 */
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
}

.map-container {
  width: 100%;
  height: 100%;
  position: relative;
  /* 防止地图被滚动 */
  touch-action: none;
  overflow: hidden;
}

.legend {
  position: absolute;
  bottom: 20px;
  left: 10px;
  background: rgba(250, 250, 250, 0.74);
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  padding: 12px;
  min-width: 180px;
  width: auto;
  z-index: 1000;
  border: 1px solid rgba(0,0,0,0.05);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  transition: opacity 0.3s;
}

.legend:hover {
  opacity: 1;
}

.legend-title {
  margin: 0 0 12px 0;
  font-size: 15px;
  font-weight: 500;
  text-align: left;
  color: #444444;
}

.legend-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 12px;
  height: 20px;
  margin-bottom: 0;
  color: rgb(56, 56, 56);
}

.legend-color {
  width: 14px;
  height: 14px;
  margin-right: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  flex-shrink: 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.error-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  text-align: center;
  z-index: 1000;
}

.error-message p {
  color: #ff3860;
  margin-bottom: 15px;
}

.error-message button {
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
}

.error-message button:hover {
  background-color: #388E3C;
}

/* 弹出框样式 */
.ol-popup {
  position: absolute;
  background-color: white;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  padding: 5px;
  border-radius: 4px;
  border: 1px solid #cccccc;
  bottom: 0px;
  left: -80px;
  min-width: 150px;
  max-width: 180px;
  z-index: 10;
  /* 添加过渡效果 */
  opacity: 0.95;
  transition: opacity 0.2s;
  /* 防止弹出框引起的滚动 */
  pointer-events: auto;
  transform: translate3d(0, 0, 0);
  /* 固定弹出框，防止影响页面滚动 */
  position: absolute !important;
  touch-action: none;
}

.ol-popup:hover {
  opacity: 1;
}

/* 弹出框箭头样式 */
.ol-popup::after, .ol-popup::before {
  top: 100%;
  border: solid transparent;
  content: "";
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
  z-index: 1;
}

.ol-popup::after {
  border-top-color: white;
  border-width: 8px;
  left: 50%;
  margin-left: -8px;
}

.ol-popup::before {
  border-top-color: #cccccc;
  border-width: 9px;
  left: 50%;
  margin-left: -9px;
}

/* 弹出框内容可点击 */
.popup-content {
  padding: 5px;
  pointer-events: auto;
  /* 防止内容引起滚动 */
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  touch-action: pan-y;
}

.hover-info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  line-height: 1.3;
}

.hover-info-item:last-child {
  margin-bottom: 0;
}

.hover-label {
  color: #666;
  font-weight: 500;
  font-size: 12px;
  min-width: 80px;
  flex-shrink: 0;
}

.hover-value {
  font-weight: 600;
  font-size: 12px;
  text-align: right;
  color: #333;
}

.back-button-container {
  position: absolute;
  top: 95px;
  left: 8px;
  z-index: 1000;
}

.back-button {
  width: 35px;
  height: 35px;
  padding: 0;
  background-color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.back-button:hover {
  background-color: rgba(255, 255, 255, 1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transform: translateY(-1px);
}

.back-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* OpenLayers缩放控件样式 - 与Leaflet样式保持一致 */
:deep(.ol-zoom) {
  position: absolute;
  top: 15px;
  left: 10px;
  z-index: 1000;
}

:deep(.ol-zoom .ol-zoom-in),
:deep(.ol-zoom .ol-zoom-out) {
  background-color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #333;
  width: 30px;
  height: 30px;
  line-height: 28px;
  text-align: center;
  text-decoration: none;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  display: block;
  margin: 0;
  padding: 0;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

:deep(.ol-zoom .ol-zoom-in) {
  border-radius: 4px 4px 0 0;
  border-bottom: none;
}

:deep(.ol-zoom .ol-zoom-out) {
  border-radius: 0 0 4px 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

:deep(.ol-zoom .ol-zoom-in:hover),
:deep(.ol-zoom .ol-zoom-out:hover) {
  background-color: rgba(255, 255, 255, 1);
  color: #000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

:deep(.ol-zoom .ol-zoom-in:active),
:deep(.ol-zoom .ol-zoom-out:active) {
  background-color: rgba(245, 245, 245, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* 移除默认的OpenLayers样式 */
:deep(.ol-control) {
  background-color: transparent;
  border-radius: 0;
  padding: 0;
}

:deep(.ol-control button) {
  background-color: transparent;
  border: none;
  outline: none;
}

.city-info-container {
  position: absolute;
  top: 10px;
  right: 20px;
  z-index: 1000;
}

.city-info {
  background: rgba(250, 250, 250, 0.74);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.city-name {
  font-size: 16px;
  font-weight: 600;
  color: #444;
}

.city-scenario {
  font-size: 16px;
  font-weight: 500;
  color: #555;
}
</style> 